import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, ClipboardList, PenTool, PlayCircle, Bug, BadgeCheck } from 'lucide-react';

const process = [
  {
    title: 'Requirement Analysis',
    description: 'We understand your application, business objectives, and quality expectations.',
    icon: Search
  },
  {
    title: 'Test Planning',
    description: 'We develop a customized testing strategy aligned with your release goals.',
    icon: ClipboardList
  },
  {
    title: 'Test Design',
    description: 'Detailed test cases and automation scripts are prepared.',
    icon: PenTool
  },
  {
    title: 'Test Execution',
    description: 'Manual and automated testing are performed across multiple environments.',
    icon: PlayCircle
  },
  {
    title: 'Defect Reporting',
    description: 'Issues are documented with clear reproduction steps and severity classification.',
    icon: Bug
  },
  {
    title: 'Validation & Sign-Off',
    description: 'Regression testing and final validation ensure your application is release-ready.',
    icon: BadgeCheck
  },
];

export const Process = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="process" ref={ref} className="py-32 bg-[#0f0f10] relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMHRlc3Rpbmd8ZW58MHx8fHwxNzg0NzMwMDYyfDA&ixlib=rb-4.1.0&q=85"
          alt="Process"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
        >
          03
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-[#D4AF37]">Process</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Simple. Structured. Reliable.
          </p>
        </motion.div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4AF37] via-[#8B1538] to-[#D4AF37]" />

          <div className="space-y-16">
            {process.map((step, index) => {
              const Icon = step.icon;
              return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-16' : 'md:pl-16'}`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-300"
                  >
                    <div className={`flex items-center gap-4 mb-3 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                      <div className="w-11 h-11 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-sm flex items-center justify-center border border-[#D4AF37]/30 flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </motion.div>
                </div>

                {/* Number Circle */}
                <div className="relative flex-shrink-0">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 180 }}
                    transition={{ duration: 0.5 }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] flex items-center justify-center border-4 border-[#0f0f10] relative z-10"
                  >
                    <span className="text-2xl font-bold text-[#1a1a1a]">{index + 1}</span>
                  </motion.div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block flex-1" />
              </motion.div>
            );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};